
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { pdfjs, Document, Page } from "react-pdf";
import { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";
import PDFViewerOverlay from "./PDFViewerOverlay";
import { Region } from "../interfaces";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface Props {
  pdfUrl: string
  regions?: Array<Region>;
  setCurrentPage?: (n: number) => void;
  setCurrentScale?: (n: number) => void;
  updateRegions?: ((regions: Array<Region>) => void);
  updatePageRects?: ((n: number, rect: DOMRect) => void);
  disabled?: boolean;
}

function PDFViewer(props: Props) {
  const { setCurrentPage, setCurrentScale, updatePageRects } = props;

  const pageWidth = 800;
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const pageNumbers: Array<number> = Array(pdfDocument?.numPages ?? 0).fill(1).map((one: number, i: number) => one + i);

  function handlePageLoad(page: PDFPageProxy) {
    if (page.pageNumber !== pageNumbers.length)
      return;

    setIsLoading(false);
    calculateVisibility();
  }

  const calculateVisibility = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer)
      return;

    let maxVisible = 0;
    let visiblePage = null;

    const pages = scrollContainer.querySelectorAll('.page');
    pages.forEach((page, index) => {
      const rect = page.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      const visibleHeight = Math.min(containerRect.bottom, rect.bottom) - Math.max(containerRect.top, rect.top);
      const visibleWidth = Math.min(containerRect.right, rect.right) - Math.max(containerRect.left, rect.left);

      const pageNumber = index + 1;

      if (visibleHeight > 0 && visibleWidth > 0) {
        const visibleArea = visibleHeight * visibleWidth;
        if (visibleArea > maxVisible) {
          maxVisible = visibleArea;
          visiblePage = pageNumber;
        }
      }

      updatePageRects?.(pageNumber, rect);
    });

    if (visiblePage) {
      if (setCurrentPage)
        setCurrentPage(visiblePage);

      if (setCurrentScale)
        pdfDocument?.getPage(visiblePage)
          .then(page => setCurrentScale(page.getViewport({ scale: 1 }).width / pageWidth));
    }

  }, [setCurrentPage, setCurrentScale, pdfDocument, updatePageRects]);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container)
      return;

    container.addEventListener('scroll', calculateVisibility);
    return () => {
      container.removeEventListener('scroll', calculateVisibility);
    };

  }, [calculateVisibility]);

  return (
    <div className={`flex flex-col h-full overflow-y-auto ${isLoading ? "bg-gray-100" : "bg-gray-50"} shadow-inner`} ref={scrollContainerRef}>
      <Document file={props.pdfUrl} onLoadSuccess={(doc) => setPdfDocument(doc)} className="flex-shrink-0" loading={<div className="bg-white rounded overflow-hidden shadow h-full" style={{ width: pageWidth }} />}>
        <div className="relative flex flex-col items-center py-5 gap-3.5" ref={pageContainerRef}>
          {pageNumbers.map((n: number) =>
            <div key={n} className="relative">
              <Page pageNumber={n} width={pageWidth} className={`rounded shadow overflow-hidden main-page-${n} page`} renderTextLayer={false} renderAnnotationLayer={false} loading={<div className="opacity-0" style={{ width: `${pageWidth}px` }} />} onLoadSuccess={handlePageLoad} />
              <PDFViewerOverlay pdfDocument={pdfDocument!} pageNumber={n} regions={props.regions} updateRegions={props.updateRegions} disabled={props.disabled} />
            </div>
          )}
        </div>
      </Document>
    </div>
  )

}

export default PDFViewer;
